/**
 * Playlist Management
 * Handles playlist UI, selection, reordering, and track management
 */

import { audioState, playlistState, lyricsState, uiState, managers } from './state.js';
import { elements } from './dom-cache.js';
import { formatTime, formatPlaylistDuration, loadTrack, startPlayback, stopPlayback, updateTrackInfo, isPlaybackBlocked } from './playback.js';
import { closeAllPanels, syncPlaylistVisibility } from './overlay-panels.js';
import { cancelDurationInitialization } from './file-handler.js';
import { escapeHtml } from '../lib/html-utils.js';

// Module-level reference to the scroll animation stop function
// Set by setupPlaylistHorizontalScroll() for external access
let _scrollStopFn = null;
let _scrollResetFn = null;

/**
 * Update playlist UI with current state
 * Sidebar is now a read-only preview - click to play track
 * @param {boolean} dispatchEvent - Whether to dispatch playlistUpdated event (default: true)
 */
export function updatePlaylistUI(dispatchEvent = true) {
  const includedCount = playlistState.playlist.filter(t => t.included).length;
  elements.playlistCount.textContent = `${includedCount} of ${playlistState.playlist.length} tracks`;
  // Also update mobile badge if it exists
  const mobileCount = document.getElementById('playlistCountMobile');
  if (mobileCount) mobileCount.textContent = elements.playlistCount.textContent;

  // Update total duration (only shown after all durations loaded)
  updatePlaylistDurationDisplay();

  // Only show included tracks in sidebar preview
  const includedTracks = playlistState.playlist.filter(t => t.included);

  elements.playlistItems.innerHTML = includedTracks.map((track) => {
    // Find original index in full playlist
    const originalIndex = playlistState.playlist.indexOf(track);
    const isPlaying = originalIndex === playlistState.currentTrackIndex;
    const hasError = track.error;

    const classes = [
      'playlist-item',
      'playlist-item-simple',
      isPlaying ? 'playing' : '',
      hasError ? 'has-error' : ''
    ].filter(Boolean).join(' ');

    const duration = hasError ? 'Error' : (track.duration ? formatTime(track.duration) : '--:--');

    // Lyrics indicator: green if has lyrics, grey if no lyrics
    const lyricsClass = track.lyrics ? 'has-lyrics' : 'no-lyrics';
    const lyricsTitle = track.lyrics ? 'Has lyrics' : 'No lyrics';

    // Error indicator
    const errorIndicator = hasError
      ? `<span class="playlist-item-error-indicator" title="${track.errorMessage || 'Error loading file'}">
           <i data-lucide="alert-circle"></i>
         </span>`
      : '';

    return `
      <li class="${classes}" data-index="${originalIndex}">
        <span class="playlist-item-number">${originalIndex + 1}</span>
        ${errorIndicator}
        <span class="playlist-item-lyrics-indicator ${lyricsClass}" title="${lyricsTitle}" ${hasError ? 'style="display:none;"' : ''}>
          <i data-lucide="music"></i>
        </span>
        <span class="playlist-item-name"><span class="playlist-item-name-text">${escapeHtml(track.name)}</span></span>
        <span class="playlist-item-duration">${duration}</span>
      </li>
    `;
  }).join('');

  // Attach simplified event listeners (just click to play)
  attachPlaylistItemListeners();

  // Recreate Lucide icons for the newly added elements
  if (window.lucide) {
    window.lucide.createIcons({ nodes: [elements.playlistItems] });
  }

  // Update track info in transport
  updateTrackInfo();

  // Update sidebar lyrics panel for the current playing track
  updateSidebarLyricsPanel();

  // Dispatch event for playlist overlay sync (unless disabled to avoid loops)
  if (dispatchEvent) {
    document.dispatchEvent(new CustomEvent('playlistUpdated'));
  }
}

/**
 * Update the sidebar playlist total duration display.
 * Hidden until all durations are loaded to avoid confusing progressive updates.
 * The entire container (icon + text) is hidden when there's nothing to show.
 */
function updatePlaylistDurationDisplay() {
  if (!elements.playlistTotalDuration) return;
  if (!playlistState.durationsLoaded || playlistState.playlist.length === 0) {
    elements.playlistTotalDuration.style.display = 'none';
    return;
  }
  const totalSeconds = playlistState.playlist
    .filter(t => t.included && t.duration)
    .reduce((sum, t) => sum + t.duration, 0);
  elements.playlistDurationText.textContent = formatPlaylistDuration(totalSeconds);
  elements.playlistTotalDuration.style.display = '';
}

/**
 * Attach event listeners to playlist items
 * Simplified: single click plays the track
 */
function attachPlaylistItemListeners() {
  elements.playlistItems.querySelectorAll('.playlist-item').forEach(item => {
    // Single click plays the track
    item.addEventListener('click', async () => {
      // Block during export
      if (isPlaybackBlocked()) return;

      // Exit live mode if active
      if (uiState.isLiveMode) {
        const { exitLiveMode } = await import('./live-mode.js');
        exitLiveMode();
      }

      const index = parseInt(item.dataset.index);
      const loaded = await loadTrack(index);
      if (loaded) {
        startPlayback(0);
      }
    });
  });
}

/**
 * Handle lyrics icon click - show panel or open file dialog
 */
export function handleLyricsIconClick(index) {
  const track = playlistState.playlist[index];
  lyricsState.lyricsPanelTrackIndex = index;

  if (!track.lyrics) {
    // No lyrics - open file dialog immediately
    elements.lyricsFileInput.click();
  } else {
    // Has lyrics - show panel
    openLyricsPanel(index);
  }
}

/**
 * Open lyrics panel for a track
 */
export function openLyricsPanel(index) {
  const track = playlistState.playlist[index];
  lyricsState.lyricsPanelTrackIndex = index;
  elements.lyricsPanelTrack.textContent = track.name;
  elements.lyricsPanelText.textContent = track.lyrics || '(No lyrics)';
  elements.lyricsPanel.style.display = 'flex';
}

/**
 * Close lyrics panel
 */
export function closeLyricsPanel() {
  elements.lyricsPanel.style.display = 'none';
  lyricsState.lyricsPanelTrackIndex = -1;
}

/**
 * Update the sidebar lyrics panel with the current track's lyrics
 */
export function updateSidebarLyricsPanel() {
  const sidebarLyricsContent = document.getElementById('sidebarLyricsContent');
  if (!sidebarLyricsContent) return;

  const currentTrack = playlistState.playlist[playlistState.currentTrackIndex];

  if (!currentTrack) {
    sidebarLyricsContent.textContent = 'No Lyrics';
    return;
  }

  if (currentTrack.lyrics) {
    sidebarLyricsContent.textContent = currentTrack.lyrics;
  } else {
    sidebarLyricsContent.textContent = 'No Lyrics';
  }
}

/**
 * Clear lyrics for the track shown in lyrics panel
 */
export function clearTrackLyrics() {
  if (lyricsState.lyricsPanelTrackIndex >= 0) {
    playlistState.playlist[lyricsState.lyricsPanelTrackIndex].lyrics = null;
    playlistState.playlist[lyricsState.lyricsPanelTrackIndex].lyricsFile = null;
    closeLyricsPanel();
    updatePlaylistUI();
    // Recalculate scroll params if this is current track
    if (lyricsState.lyricsPanelTrackIndex === playlistState.currentTrackIndex) {
      lyricsState.currentLyricsScrollParams = null;
    }
  }
}

/**
 * Clear playlist and reset to initial state
 */
export function clearPlaylist() {
  // Block playlist modifications during export
  if (isPlaybackBlocked()) return;

  stopPlayback();
  cancelDurationInitialization(); // Stop any background duration processing
  playlistState.playlist = [];
  playlistState.selectedIndices = [];
  playlistState.currentTrackIndex = 0;
  audioState.audioBuffer = null;

  closeAllPanels();

  // Clear visualizer
  managers.visualizerManager.clear();

  // Sync all playlist-related UI (drop modal, transport bar, export button)
  syncPlaylistVisibility();

  // Legacy support: also update old elements if they exist
  if (elements.playlistContainer) elements.playlistContainer.style.display = 'none';
  if (elements.dropZone) elements.dropZone.style.display = 'block';
  if (elements.audioControls) elements.audioControls.style.display = 'none';
  if (elements.exportControls) elements.exportControls.style.display = 'none';
}

/**
 * Toggle playlist collapse state
 */
export function togglePlaylistCollapse() {
  elements.playlistContainer.classList.toggle('playlist-collapsed');
}

/**
 * Set up playlist button event listeners
 */
export function setupPlaylistButtonListeners() {
  // Helper to safely add listener if element exists
  const addListener = (id, event, handler) => {
    const el = document.getElementById(id);
    if (el) el.addEventListener(event, handler);
  };

  // Edit playlist button - opens overlay and closes sidebar
  addListener('editPlaylistBtn', 'click', handleEditPlaylist);

  // Clear playlist with confirmation
  addListener('clearPlaylistBtn', 'click', handleClearPlaylistClick);

  // Legacy: collapse button (if still exists)
  addListener('collapsePlaylistBtn', 'click', togglePlaylistCollapse);

  if (elements.playlistHeader) {
    elements.playlistHeader.addEventListener('click', (e) => {
      if (e.target.id !== 'collapsePlaylistBtn') togglePlaylistCollapse();
    });
  }
}

/**
 * Handle edit playlist button click
 * Opens the overlay playlist and closes the sidebar panel
 */
async function handleEditPlaylist() {
  // Import dynamically to avoid circular dependencies
  const { openOverlay } = await import('./playlist-overlay.js');
  const { closeAllPanels } = await import('./overlay-panels.js');

  // Close the sidebar panel
  closeAllPanels();

  // Open the playlist overlay
  openOverlay();
}

/**
 * Handle clear playlist button click with confirmation
 */
function handleClearPlaylistClick() {
  const trackCount = playlistState.playlist.length;
  if (trackCount === 0) return;

  const confirmed = window.confirm(
    `Clear playlist?\n\nThis will remove ${trackCount} track${trackCount !== 1 ? 's' : ''} from the playlist.`
  );

  if (confirmed) {
    clearPlaylist();
  }
}

/**
 * Setup sync between sidebar and overlay playlists
 * This ensures the sidebar updates when the overlay makes changes
 */
export function setupPlaylistSync() {
  // Flag to track if we're currently updating (to avoid infinite loops)
  let isUpdating = false;

  // Listen for playlistUpdated events — sync sidebar UI and playlist visibility
  document.addEventListener('playlistUpdated', () => {
    // Avoid infinite loop: if we're already updating, skip
    if (isUpdating) return;

    isUpdating = true;
    updatePlaylistUI(false);
    syncPlaylistVisibility();
    isUpdating = false;
  });

  // Also sync when selection changes
  document.addEventListener('playlistSelectionChanged', () => {
    if (isUpdating) return;

    isUpdating = true;
    updatePlaylistUI(false);
    isUpdating = false;
  });

  // Refresh duration display when all durations finish loading
  document.addEventListener('playlistDurationsLoaded', () => {
    updatePlaylistDurationDisplay();
  });

  console.log('[Playlist] Sync with overlay enabled');
}

/**
 * Setup horizontal scroll zones for playlist item names
 * When hovering in the right 25% zone, scrolls text left to reveal more
 * When hovering in the left 25% zone, scrolls text right back to start
 */
export function setupPlaylistHorizontalScroll() {
  const playlistScroll = document.querySelector('.playlist-scroll');
  if (!playlistScroll) return;

  // Scroll state
  let scrollOffset = 0;
  let scrollAnimationId = null;
  let isScrolling = false;
  let preserveScrollOnUpdate = false; // Flag to preserve scroll during duration updates
  let currentRelativeX = 0.5; // Track cursor position for variable speed
  const MIN_SCROLL_SPEED = 0.5; // pixels per frame at zone boundary
  const MAX_SCROLL_SPEED = 12; // pixels per frame at edge
  const LEFT_ZONE_THRESHOLD = 0.25; // 0-25%
  const RIGHT_ZONE_THRESHOLD = 0.75; // 75-100%

  /**
   * Calculate scroll speed based on cursor position within the zone.
   * Speed increases as cursor gets closer to the edge.
   * @param {number} direction - 1 for right zone, -1 for left zone
   * @returns {number} Scroll speed in pixels per frame
   */
  function getScrollSpeed(direction) {
    let zoneProgress;
    if (direction > 0) {
      // Right zone: 0.75 → 1.0, progress 0 → 1
      zoneProgress = (currentRelativeX - RIGHT_ZONE_THRESHOLD) / (1 - RIGHT_ZONE_THRESHOLD);
    } else {
      // Left zone: 0.25 → 0, progress 0 → 1
      zoneProgress = (LEFT_ZONE_THRESHOLD - currentRelativeX) / LEFT_ZONE_THRESHOLD;
    }
    // Clamp to 0-1 range
    zoneProgress = Math.max(0, Math.min(1, zoneProgress));
    return MIN_SCROLL_SPEED + (MAX_SCROLL_SPEED - MIN_SCROLL_SPEED) * zoneProgress;
  }

  /**
   * Get the maximum scroll offset (how much overflow exists)
   */
  function getMaxScrollOffset() {
    const nameElements = playlistScroll.querySelectorAll('.playlist-item-name');
    let maxOverflow = 0;

    nameElements.forEach(container => {
      const textEl = container.querySelector('.playlist-item-name-text');
      if (textEl) {
        const overflow = textEl.scrollWidth - container.clientWidth;
        if (overflow > maxOverflow) {
          maxOverflow = overflow;
        }
      }
    });

    return maxOverflow;
  }

  /**
   * Apply scroll offset to all playlist item names
   */
  function applyScrollOffset(offset) {
    const textElements = playlistScroll.querySelectorAll('.playlist-item-name-text');
    textElements.forEach(el => {
      el.style.transform = `translateX(${-offset}px)`;
    });
  }

  /**
   * Start scrolling in a direction
   * @param {number} direction - 1 for right (reveal more), -1 for left (back to start)
   */
  function startScrolling(direction) {
    if (scrollAnimationId) return;

    isScrolling = true;
    preserveScrollOnUpdate = true; // User is actively scrolling
    playlistScroll.classList.add('scroll-active');

    function animate() {
      const maxOffset = getMaxScrollOffset();

      // Update scroll offset with variable speed based on cursor position
      scrollOffset += getScrollSpeed(direction) * direction;

      // Clamp to bounds
      if (scrollOffset < 0) {
        scrollOffset = 0;
        applyScrollOffset(scrollOffset);
        stopScrolling();
        return;
      }
      if (scrollOffset > maxOffset) {
        scrollOffset = maxOffset;
        applyScrollOffset(scrollOffset);
        stopScrolling();
        return;
      }

      applyScrollOffset(scrollOffset);
      scrollAnimationId = requestAnimationFrame(animate);
    }

    scrollAnimationId = requestAnimationFrame(animate);
  }

  /**
   * Stop scrolling animation
   */
  function stopScrolling() {
    if (scrollAnimationId) {
      cancelAnimationFrame(scrollAnimationId);
      scrollAnimationId = null;
    }
    isScrolling = false;

    // Keep scroll-active class if text is still scrolled
    if (scrollOffset === 0) {
      playlistScroll.classList.remove('scroll-active');
    }
  }

  /**
   * Reset scroll to beginning
   */
  function resetScroll() {
    stopScrolling();
    scrollOffset = 0;
    applyScrollOffset(0);
    playlistScroll.classList.remove('scroll-active');
    preserveScrollOnUpdate = false;
  }

  // Expose stop/reset functions at module level for external access (e.g., during export)
  _scrollStopFn = stopScrolling;
  _scrollResetFn = resetScroll;

  /**
   * Restore scroll position after DOM update
   * @param {number} savedOffset - The scroll offset to restore (passed explicitly to avoid race conditions)
   */
  function restoreScrollPosition(savedOffset) {
    if (savedOffset > 0) {
      const maxOffset = getMaxScrollOffset();

      // Safety check: if maxOffset is 0, layout may not be complete yet
      // In this case, apply the saved offset directly without clamping
      // The offset will be properly clamped on the next scroll interaction
      if (maxOffset > 0 && savedOffset > maxOffset) {
        scrollOffset = maxOffset;
      } else {
        scrollOffset = savedOffset;
      }

      applyScrollOffset(scrollOffset);
      if (scrollOffset > 0) {
        playlistScroll.classList.add('scroll-active');
      }
    }
  }

  // Handle mouse movement within playlist
  playlistScroll.addEventListener('mousemove', (e) => {
    const rect = playlistScroll.getBoundingClientRect();
    currentRelativeX = (e.clientX - rect.left) / rect.width;

    // Check which zone the cursor is in
    if (currentRelativeX >= RIGHT_ZONE_THRESHOLD) {
      // Right zone - scroll to reveal more text (scroll left)
      if (!isScrolling || scrollAnimationId === null) {
        stopScrolling();
        startScrolling(1);
      }
    } else if (currentRelativeX <= LEFT_ZONE_THRESHOLD) {
      // Left zone - scroll back to start (scroll right)
      if (!isScrolling || scrollAnimationId === null) {
        stopScrolling();
        startScrolling(-1);
      }
    } else {
      // Middle zone - stop scrolling
      stopScrolling();
    }
  });

  // Stop scrolling when mouse leaves playlist area
  playlistScroll.addEventListener('mouseleave', () => {
    stopScrolling();
    // Reset preserve flag when mouse leaves - allows reset on next update without hover
    preserveScrollOnUpdate = false;
  });

  // Handle playlist updates - preserve scroll position when possible
  document.addEventListener('playlistUpdated', () => {
    // Check if mouse is currently over the playlist area
    const isHovering = playlistScroll.matches(':hover');

    if (isHovering || preserveScrollOnUpdate) {
      // Capture the current scroll offset before any async operations
      // This prevents race conditions where scrollOffset might change
      const savedOffset = scrollOffset;

      // Use double requestAnimationFrame to ensure layout is complete
      // First rAF: schedules callback after current frame's style/layout
      // Second rAF: ensures we're in a new frame with complete layout
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          restoreScrollPosition(savedOffset);
        });
      });
    } else {
      // Not hovering and not preserving - reset scroll
      resetScroll();
    }
  });

  console.log('[Playlist] Horizontal scroll zones enabled');
}

/**
 * Setup mobile collapsible panels for Playlist and Lyrics
 * Only one panel can be expanded at a time on mobile
 */
export function setupMobileCollapsiblePanels() {
  const playlistCollapsible = document.getElementById('playlistCollapsible');
  const lyricsCollapsible = document.getElementById('lyricsCollapsible');

  if (!playlistCollapsible || !lyricsCollapsible) return;

  const playlistHeader = playlistCollapsible.querySelector('.mobile-collapsible-header');
  const lyricsHeader = lyricsCollapsible.querySelector('.mobile-collapsible-header');

  /**
   * Expand the target section and collapse the other
   */
  function setExpanded(targetSection) {
    const otherSection = targetSection === playlistCollapsible ? lyricsCollapsible : playlistCollapsible;

    targetSection.classList.add('expanded');
    targetSection.classList.remove('collapsed');
    otherSection.classList.add('collapsed');
    otherSection.classList.remove('expanded');

    // Recreate icons after toggle
    if (window.lucide) {
      window.lucide.createIcons({ nodes: [targetSection, otherSection] });
    }
  }

  /**
   * Toggle a collapsible section
   * When one expands, the other collapses
   */
  function toggleSection(section) {
    const isExpanded = section.classList.contains('expanded');

    if (isExpanded) {
      // Don't allow collapsing the last expanded section
      return;
    }

    setExpanded(section);
  }

  // Add click handlers
  if (playlistHeader) {
    playlistHeader.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleSection(playlistCollapsible);
    });
  }

  if (lyricsHeader) {
    lyricsHeader.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleSection(lyricsCollapsible);
    });
  }

  // Default: playlist expanded, lyrics collapsed
  setExpanded(playlistCollapsible);

  console.log('[Playlist] Mobile collapsible panels enabled');
}

/**
 * Pause playlist animations for export.
 * Stops the horizontal scroll animation to free GPU resources.
 */
export function pausePlaylistAnimations() {
  if (_scrollStopFn) {
    _scrollStopFn();
  }
}

/**
 * Resume playlist animations after export.
 * Currently no-op since scroll only runs on hover.
 */
export function resumePlaylistAnimations() {
  // Scroll animation only runs on hover, so nothing to resume
  // Reset scroll position to avoid stuck state
  if (_scrollResetFn) {
    _scrollResetFn();
  }
}
